import { ReactElement, useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  HiOutlineCheckCircle,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiXMark,
} from 'react-icons/hi2';

import { FormProvider, useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useCreateConfig, useUpdateConfig, useDeleteConfig, useBatchUpdateConfigs } from '../../../../hooks/useConfigs';
import { useUpdateRun } from '../../../../hooks/useRuns';
import { useReprocess } from '../../../../hooks/useReprocess';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';
import Spinner from '../../../../components/Spinner';
import SessionRunOrder from './SessionRunOrder';
import { useInvalidateSession, useUpdateSession } from '../../../../hooks/useSessions';
import { useUnsavedChangesWarning } from '../../../../hooks/useUnsavedChangesWarning';
import { usePersistentViewState } from '../../../../hooks/usePersistentViewState';
import { v4 as uuidv4 } from 'uuid';
import { numberToLetters } from '../../../../utils/letterNumber';
import { DISC_LETTER_NUMBER } from '../../../../constants/apiConstants';
import ButtonList from 'components/ButtonList';
import SectionHeader from 'components/SectionHeader';
import { Session } from 'types/Session';
import SessionConfigsList from './SessionConfigsList';
import SessionEquipmentList from './SessionEquipmentList';
import SessionCalibrations from './SessionCalibrations';

export type EditOperationMode = 'idle' | 'create' | 'delete' | 'reorder' | 'run_move';

export function isOperationLocked(editOpMode: EditOperationMode | undefined, allowedMode: EditOperationMode): boolean {
  if (editOpMode == null) return false;
  return editOpMode !== 'idle' && editOpMode !== allowedMode;
}

export default function SessionConfigs(): ReactElement {
  const { session, isSessionFetching } = useOutletContext<{
    session: any;
    isSessionFetching: boolean;
  }>();

  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [viewType, setViewType] = usePersistentViewState({
    sessionId: session?.id || 'default',
    defaultView: 'config',
  });
  const [showAllRuns, setShowAllRuns] = useState(true);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [configMovesToSave, setConfigMovesToSave] = useState<{ [key: string]: () => Promise<void> }>({});
  const [changedConfigIds, setChangedConfigIds] = useState<Set<string>>(new Set());
  const [editOpMode, setEditOpMode] = useState<EditOperationMode>('idle');

  // Ref to store equipment save function
  const equipmentSaveRef = useRef<(() => Promise<void>) | null>(null);

  const invalidateSession = useInvalidateSession(session.id);

  // State management for config flags
  const formMethods = useForm<Session>({ defaultValues: session });
  const {
    insert: insertConfigControl,
    replace: replaceConfigControls,
    fields: configControls,
  } = useFieldArray({
    control: formMethods.control,
    name: 'configs',
    keyName: 'fieldId',
  });

  useEffect(() => {
    formMethods.reset(session);
  }, [session]);

  const { dirtyFields } = useFormState({ control: formMethods.control });
  const updateConfig = useUpdateConfig();
  const batchUpdateConfigs = useBatchUpdateConfigs();
  const createConfig = useCreateConfig();
  const deleteConfig = useDeleteConfig();
  const updateRun = useUpdateRun();
  const updateSession = useUpdateSession(session.id);

  // Use reprocess hook
  const { handleReprocess, addConfigToReprocess, removeConfigFromReprocess, removeAllReprocess, configsToReprocess } =
    useReprocess();

  const runOrder = session?.run_order || [];
  const [plannedRunConfigIds, setPlannedRunConfigIds] = useState(runOrder);

  const runs = configControls.flatMap((config: any) => config?.runs);
  const nextRunIndex = runs?.length || 0;

  // Use unsaved changes warning hook at SessionConfigs level
  const { clearUnsavedChanges, markUnsavedChange, clearUnsavedChange } = useUnsavedChangesWarning({
    enabled: isEdit,
    message: 'You have unsaved changes to this session configuration. Are you sure you want to leave?',
    formMethods: formMethods,
  });

  // Track changes to plannedRunConfigIds as manual changes
  useEffect(() => {
    const hasRunOrderChanged = JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order);
    if (hasRunOrderChanged) {
      markUnsavedChange('runOrder');
    } else {
      clearUnsavedChange('runOrder');
    }
  }, [plannedRunConfigIds, session.run_order, markUnsavedChange, clearUnsavedChange]);

  // Handle adding/removing configs from reprocess list
  const handleReprocessConfigChange = (configId: string, shouldReprocess: boolean) => {
    const config = session.configs.find((c: any) => c.id === configId);
    if (!config) return;

    if (shouldReprocess) {
      addConfigToReprocess(config);
    } else {
      removeConfigFromReprocess(configId);
    }
  };

  const save = async () => {
    setIsSaving(true);
    const formValues = formMethods.getValues();
    const formConfigs = formValues.configs;

    // Check if we need to send new_order for reordering
    const isReorderOperation = changedConfigIds.size > 0;
    if (isReorderOperation) {
      await saveWithReorder(formConfigs);
    } else {
      await saveRunOrder();
    }

    // Save equipment changes if available
    if (equipmentSaveRef.current) {
      await equipmentSaveRef.current();
    }
    await saveDeletedConfigs(formConfigs);
    await saveNewConfigs(formConfigs);
    // Only save existing configs if we didn't just do a reorder operation
    // (reorder already updates configs via the new_order endpoint)
    if (!isReorderOperation) {
      await saveExistingConfigs(formConfigs);
    }
    await removeDeletedConfigControls(formConfigs);
    await saveConfigMovesToSave();
    // Asynchronously reprocess runs
    handleReprocess(invalidateSession);

    // Invalidate session to refresh data after deletions/updates
    invalidateSession();

    clearUnsavedChanges();
    setChangedConfigIds(new Set()); // Clear changed configs
    setEditOpMode('idle');

    setIsEdit(false);
    setIsSaving(false);
  };

  const saveWithReorder = async (formConfigs: any[]) => {
    try {
      // Create new_order array with config IDs in their current order
      const newOrder = formConfigs
        .filter((config) => config.letter_number !== DISC_LETTER_NUMBER && !config.isDeleted)
        .map((config) => config.id);

      const sessionUpdates: any = {
        testsession_id: session.id,
        new_order: newOrder,
      };

      // Check if run order has also changed
      if (JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order)) {
        sessionUpdates.run_order = plannedRunConfigIds;
      }

      await updateSession.mutateAsync(sessionUpdates);
    } catch (e) {
      console.error(e);
      toast.error('Error reordering configurations');
      return;
    }
  };

  const checkSessionChanges = () => {
    const sessionUpdates: any = {};

    // Check if run order has changed
    if (JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order)) {
      sessionUpdates.run_order = plannedRunConfigIds;
    }

    return sessionUpdates;
  };

  const saveRunOrder = async () => {
    // Use the centralized session change detection
    const sessionUpdates = checkSessionChanges();

    // Only make API call if there are updates
    if (Object.keys(sessionUpdates).length > 0) {
      try {
        await updateSession.mutateAsync({
          id: session.id,
          ...sessionUpdates,
        });
      } catch (e) {
        console.error(e);
        toast.error('Error updating session');
        return;
      }
    }
  };

  const saveNewConfigs = async (formConfigs: any[]) => {
    try {
      for (const [index, config] of formConfigs.entries()) {
        // POST (create)
        if (config.isNew && !config.isDeleted) {
          await createConfig.mutateAsync({ ...config, testsession_id: session.id });
          formMethods.setValue(`configs.${index}.isNew`, false);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error creating config(s)');
      return;
    }
  };

  const saveExistingConfigs = async (formConfigs: any[]) => {
    try {
      if (dirtyFields.configs) {
        const configsToUpdate: any[] = [];

        // Helper function to validate and clean UUID fields
        const validateUUIDFields = (config: any) => {
          const uuidFields = [
            'baseline_id',
            'frame_id',
            'tyre_front_id',
            'tyre_rear_id',
            'wheel_front_id',
            'wheel_rear_id',
          ];

          uuidFields.forEach((field) => {
            if (!config[field]) {
              config[field] = null; // This will write "" to null
            } else if (config[field] && typeof config[field] === 'string' && config[field].length < 32) {
              config[field] = null;
            }
          });
        };

        // Collect all configs that need updates
        for (const index of Object.keys(dirtyFields.configs)) {
          const numericIndex = parseInt(index);
          const configValues = formConfigs[numericIndex];

          // Only update configs that have an id (i.e., not new/unsaved configs) and are not marked for deletion
          if (configValues && configValues.id && !configValues.isDeleted && !configValues.isNew) {
            // Find the original config from session to compare
            const originalConfig = session.configs.find((c: any) => c.id === configValues.id);

            // Check if the config has actually changed by comparing key fields
            const hasActualChanges =
              !originalConfig ||
              originalConfig.letter_number !== configValues.letter_number ||
              originalConfig.description !== configValues.description ||
              originalConfig.baseline_id !== configValues.baseline_id ||
              originalConfig.autocal_skip !== configValues.autocal_skip ||
              originalConfig.autocal_lock !== configValues.autocal_lock ||
              JSON.stringify(originalConfig.autocal_list) !== JSON.stringify(configValues.autocal_list) ||
              // Equipment fields
              originalConfig.frame_id !== configValues.frame_id ||
              originalConfig.tyre_front_id !== configValues.tyre_front_id ||
              originalConfig.tyre_rear_id !== configValues.tyre_rear_id ||
              originalConfig.wheel_front_id !== configValues.wheel_front_id ||
              originalConfig.wheel_rear_id !== configValues.wheel_rear_id ||
              originalConfig.tyre_front_pressure !== configValues.tyre_front_pressure ||
              originalConfig.tyre_rear_pressure !== configValues.tyre_rear_pressure ||
              JSON.stringify(originalConfig.dampers) !== JSON.stringify(configValues.dampers);

            if (hasActualChanges) {
              // Clean up fields that shouldn't be sent to the backend
              const cleanedConfigValues = { ...configValues };
              delete cleanedConfigValues.autocal_branch;
              validateUUIDFields(cleanedConfigValues);

              configsToUpdate.push(cleanedConfigValues);
            }
          }
        }

        // Batch update all configs that have changes
        if (configsToUpdate.length > 0) {
          const updatedConfigs = await batchUpdateConfigs.mutateAsync(configsToUpdate);

          // Update form configs with the returned data if needed
          if (updatedConfigs) {
            for (const index of Object.keys(dirtyFields.configs)) {
              const numericIndex = parseInt(index);
              const configValues = formConfigs[numericIndex];

              if (configValues && configValues.id) {
                const updatedConfig = Array.isArray(updatedConfigs)
                  ? updatedConfigs.find((c: any) => c.id === configValues.id)
                  : updatedConfigs;
                if (updatedConfig) {
                  formConfigs[numericIndex] = { ...formConfigs[numericIndex], ...updatedConfig };
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving configs');
      return;
    }
  };

  const saveDeletedConfigs = async (formConfigs: any[]) => {
    // Delete configs marked for deletion
    try {
      for (const config of formConfigs) {
        if (config.isDeleted && config.id && !config.isNew) {
          await deleteConfig.mutateAsync(config.id);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Error deleting config(s)');
      return;
    }
  };
  const removeDeletedConfigControls = (formConfigs: any[]) => {
    const remainingConfigs: any[] = [];
    for (const config of formConfigs) {
      if (!config.isDeleted) {
        remainingConfigs.push(config);
      }
    }

    // Remove deleted configs from form controls
    try {
      replaceConfigControls(remainingConfigs);
    } catch (e) {
      console.error(e);
      toast.error('Error removing config(s)');
      return;
    }
  };

  const saveConfigMovesToSave = async () => {
    for (const configId in configMovesToSave) {
      await configMovesToSave[configId]();
    }
  };

  const cancel = () => {
    // Add isDeleted field to all configs if it doesn't exist
    formMethods.reset(session);
    setPlannedRunConfigIds(session.run_order || []);
    setIsEdit(false);
    removeAllReprocess();
    setConfigMovesToSave({});
    setEditOpMode('idle');

    clearUnsavedChanges();
  };

  const defaultDescription = (letterNumber: number): string => {
    return `Config ${numberToLetters(letterNumber)}`;
  };

  const insertNewConfig = () => {
    if (isOperationLocked(editOpMode, 'create')) return;
    setEditOpMode('create');
    const nextLetterNumber = reorderConfigLetters();

    const validConfigs = configControls.filter(
      (config: any) => config.letter_number < DISC_LETTER_NUMBER || config.letter_number === undefined,
    );

    const lastConfig = validConfigs.length > 0 ? validConfigs[validConfigs.length - 1] : null;

    const newConfigId = uuidv4();
    insertConfigControl(validConfigs.length, {
      id: newConfigId,
      letter_number: nextLetterNumber,
      description: defaultDescription(nextLetterNumber),
      baseline_id: lastConfig ? (lastConfig as any).baseline_id || (lastConfig as any).id : null,
      runs: [],
      isDeleted: false,
      isNew: true,
    });

    // Focus on the new config
    setTimeout(() => {
      formMethods.setFocus(`configs.${validConfigs.length}.description`);
    }, 100);
  };

  const reorderConfigLetters = (): number => {
    // Go through all configs and assign sequential letters to non-deleted ones
    let currentLetterNumber = 0; // Start at 0 which corresponds to 'A'

    configControls.forEach((config: any, index: number) => {
      if (formMethods.getValues(`configs.${index}.isDeleted`)) {
        // Set deleted configs to undefined letter_number
        formMethods.setValue(`configs.${index}.letter_number`, undefined);
      } else {
        // Assign sequential letter_number to non-deleted configs
        if (config.letter_number != DISC_LETTER_NUMBER) {
          if (config.letter_number !== currentLetterNumber) {
            if (config.description === defaultDescription(config.letter_number)) {
              formMethods.setValue(`configs.${index}.description`, defaultDescription(currentLetterNumber));
            }
            formMethods.setValue(`configs.${index}.letter_number`, currentLetterNumber);
          }
          // Only increment letter_number for non-deleted configs
          currentLetterNumber += 1;
        } else {
          formMethods.setValue(`configs.${index}.letter_number`, DISC_LETTER_NUMBER);
        }
      }
    });

    // Mark form as dirty
    formMethods.setValue('configs', formMethods.getValues('configs'), { shouldDirty: true });
    return currentLetterNumber;
  };

  const updateNextConfigId = (nextConfigId: string) => {
    // Find the first instance of nextConfigId and move it to the front
    setPlannedRunConfigIds((prev: string[]) => {
      const index = prev.findIndex((id: string) => id === nextConfigId);
      if (index === -1) return prev;
      const newArray = [...prev];
      const [movedItem] = newArray.splice(index, 1);
      return [movedItem, ...newArray];
    });
  };

  // Extract options into a separate component
  const OptionsButtons = () => {
    const optionsContainerClasses = [
      'cr-flex',
      'cr-w-full',
      'cr-flex-col',
      'cr-gap-2',
      'md:cr-flex-row',
      'md:cr-items-center',
      'md:cr-justify-between',
      'md:cr-gap-0',
    ].join(' ');

    const buildToggleButtonClasses = (target: 'config' | 'run' | 'equipment') => {
      const baseClasses = [
        '!cr-w-32',
        '!cr-h-11',
        '!cr-border-0',
        '!cr-rounded-md',
        '!cr-px-3',
        '!cr-py-2',
        '!cr-text-base',
        '!cr-font-semibold',
        '!cr-transition-colors',
        '!cr-duration-200',
        '!cr-normal-case',
        '!cr-flex',
        '!cr-items-center',
        '!cr-justify-center',
        '!cr-whitespace-nowrap',
        '!cr-shadow-sm',
        'font-forma',
        viewType === target
          ? '!cr-bg-[#03365D] !cr-text-white'
          : '!cr-bg-[#B0C9D1] !cr-text-[#03365D] hover:!cr-bg-[#d3d3d3]',
      ];

      return baseClasses.join(' ');
    };

    return (
      <div className={optionsContainerClasses}>
        {/* Left side: Edit/Save/Cancel */}
        <ButtonList className="cr-flex-wrap !cr-gap-2 md:cr-flex-nowrap">
          {!isEdit && (
            <>
              <Button
                onClick={() => setIsEdit(true)}
                icon={<HiOutlinePencilSquare className={'cr-size-7'} />}
                className="secondary-button"
              >
                Edit
              </Button>
            </>
          )}
          {isEdit && (
            <>
              <Button
                onClick={formMethods.handleSubmit(save)}
                icon={<HiOutlineCheckCircle className={'cr-size-7'} />}
                className={editOpMode !== 'idle'
                  ? 'secondary-button !cr-bg-green-500 !cr-text-white !cr-border-green-500'
                  : 'secondary-button'}
              >
                Save
              </Button>
              <Button onClick={cancel} icon={<HiXMark className={'cr-size-7'} />} className="secondary-button">
                Cancel
              </Button>
            </>
          )}
        </ButtonList>
        {/* Right side: New Config/Run Order or Edit */}
        <ButtonList className="cr-w-full cr-flex-wrap cr-justify-start !cr-gap-2 md:cr-w-auto md:cr-flex-nowrap md:cr-justify-end">
          {isEdit && !isReordering && (
            <Button
              onClick={() => insertNewConfig()}
              icon={<HiOutlinePlus className={'cr-size-5'} />}
              className="secondary-button"
              disabled={isOperationLocked(editOpMode, 'create')}
            >
              New Config
            </Button>
          )}
          <div className="cr-flex cr-flex-wrap cr-gap-2 md:cr-flex-nowrap">
            <Button
              onClick={() => {
                setViewType('config');
                setIsReordering(false);
              }}
              className={buildToggleButtonClasses('config')}
            >
              <span style={{ fontFamily: 'Poppins, sans-serif' }}>Config List</span>
            </Button>
            <Button
              onClick={() => {
                setViewType('run');
                setIsReordering(true);
              }}
              className={buildToggleButtonClasses('run')}
            >
              <span style={{ fontFamily: 'Poppins, sans-serif' }}>Run List</span>
            </Button>
            <Button
              onClick={() => {
                setViewType('equipment');
                setIsReordering(false);
              }}
              className={buildToggleButtonClasses('equipment')}
            >
              <span style={{ fontFamily: 'Poppins, sans-serif' }}>Equip List</span>
            </Button>
          </div>
        </ButtonList>
      </div>
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <SectionHeader
          title="Session Configurations"
          isLoading={
            isSessionFetching || updateConfig.isPending || updateConfig.isPending || updateRun.isPending || isSaving
          }
          className="!cr-mt-14"
          inset
        />
        <form>
          <fieldset
            disabled={
              isSessionFetching || updateConfig.isPending || updateConfig.isPending || updateRun.isPending || isSaving
            }
          >
            <div className="cr-flex cr-flex-row cr-gap-4 cr-py-4 cr-font-main cr-text-[16px] cr-text-lg">
              <OptionsButtons />
            </div>
            {viewType === 'config' && (
              <SessionConfigsList
                session={session}
                isEdit={isEdit}
                configControls={configControls}
                showAllRuns={showAllRuns}
                setShowAllRuns={setShowAllRuns}
                updateNextConfigId={updateNextConfigId}
                plannedRunConfigIds={plannedRunConfigIds}
                setPlannedRunConfigIds={setPlannedRunConfigIds}
                nextRunIndex={nextRunIndex}
                activeRunId={activeRunId}
                setActiveRunId={setActiveRunId}
                invalidateSession={invalidateSession}
                handleReprocessConfigChange={handleReprocessConfigChange}
                configsToReprocess={configsToReprocess}
                reorderConfigLetters={reorderConfigLetters}
                configMovesToSave={configMovesToSave}
                changedConfigIds={changedConfigIds}
                editOpMode={editOpMode}
                setEditOpMode={setEditOpMode}
                setChangedConfigIds={setChangedConfigIds}
              />
            )}
            {viewType === 'run' && (
              <SessionRunOrder
                key="run-orders"
                configs={configControls}
                plannedRunConfigIds={plannedRunConfigIds}
                setPlannedRunConfigIds={setPlannedRunConfigIds}
                isEdit={isEdit}
              />
            )}
            {viewType === 'equipment' && isEdit && (
              <SessionEquipmentList
                session={session}
                configControls={configControls}
                markUnsavedChange={markUnsavedChange}
                clearUnsavedChange={clearUnsavedChange}
                equipmentSaveRef={equipmentSaveRef}
              />
            )}
            {viewType === 'equipment' && !isEdit && (
              <SessionEquipmentList
                session={session}
                configControls={configControls}
                readOnly={true}
                markUnsavedChange={markUnsavedChange}
                clearUnsavedChange={clearUnsavedChange}
                equipmentSaveRef={equipmentSaveRef}
              />
            )}
            <div className="cr-flex cr-flex-row cr-gap-4 cr-py-4 cr-font-main cr-text-[16px] cr-text-lg">
              <OptionsButtons />
              <Spinner
                isFetching={
                  isSessionFetching || updateConfig.isPending || updateConfig.isPending || updateRun.isPending
                }
              />
            </div>
            <div className="cr-h-10" />
          </fieldset>
        </form>
        {/* Display calibration information from configs where branch == true */}
      </FormProvider>
      {session && session.configs && session.configs.length > 0 && <SessionCalibrations session={session} />}
    </>
  );
}
